import { Undo2 } from 'lucide-react';

interface RoastProfileCellInputProps {
  dataTestId: string;
  value: string | number;
  manual: boolean;
  readOnly: boolean;
  onChange: (value: string) => void;
  /** When true, only digits are accepted (used for the drum RPM column). */
  numericOnly?: boolean;
  /** True when this cell no longer matches the imported profile. */
  edited?: boolean;
  /** Tooltip for the revert control, e.g. "Imported value 15 — click to restore". */
  revertLabel?: string;
  /** Restores this single cell to its imported value. */
  onRevert?: () => void;
}

/** A single editable cell in the roast profile table (temp/fire/airflow/etc). */
export function RoastProfileCellInput({
  dataTestId,
  value,
  manual,
  readOnly,
  onChange,
  numericOnly = false,
  edited = false,
  revertLabel,
  onRevert,
}: RoastProfileCellInputProps) {
  const showRevert = edited && !readOnly && !!onRevert;

  return (
    <td className={`border p-1 relative group/cell ${edited ? 'bg-edited-50/60' : ''}`}>
      <input
        data-test-id={dataTestId}
        data-edited={edited || undefined}
        className={`ios-no-autozoom-input w-full text-center outline-none bg-transparent ${
          edited ? 'text-edited-800 font-semibold' : ''
        } ${manual && !edited ? 'bg-amber-50' : ''}`}
        disabled={readOnly}
        value={value}
        onChange={(e) => {
          const next = e.target.value;
          if (numericOnly && next !== '' && !/^\d*$/.test(next)) return;
          onChange(next);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
      />

      {/* Dog-ear: a folded page corner marks a value touched by hand. It costs no
          row height, so it scans down a dense column without loosening the table. */}
      {edited && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-0 h-0 w-0 border-l-[7px] border-t-[7px] border-l-transparent border-t-edited-800"
        />
      )}

      {showRevert && (
        <button
          type="button"
          onClick={onRevert}
          title={revertLabel}
          aria-label={revertLabel}
          data-test-id={`${dataTestId}-revert`}
          className="absolute left-0.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-edited-700 opacity-0 transition-opacity hover:text-edited-900 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-edited-800 group-hover/cell:opacity-100 motion-reduce:transition-none"
        >
          <Undo2 className="h-3 w-3" />
        </button>
      )}
    </td>
  );
}
